import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Briefcase, Calendar, Euro, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { imageForExpertise } from "../lib/vacancyMeta";

export default function VacatureDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/vacancies/${id}`).then(({ data }) => setV(data)).catch(() => setV(false));
  }, [id]);

  const apply = async () => {
    if (!user || user === false) { nav("/login"); return; }
    if (user.role !== "candidate") { toast.error("Alleen kandidaten kunnen solliciteren."); return; }
    setBusy(true);
    try {
      await api.post("/applications", { vacancy_id: id });
      toast.success("Sollicitatie verstuurd!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sollicitatie mislukt");
    }
    setBusy(false);
  };

  const save = async () => {
    if (!user || user === false) { nav("/login"); return; }
    if (user.role !== "candidate") return;
    try {
      await api.post(`/saved-vacancies/${id}`);
      toast.success("Opgeslagen");
    } catch (_) { /* ignore */ }
  };

  if (v === null) return <div className="ok-container py-20 text-sm text-[#4a5d5e]">Laden…</div>;
  if (v === false) return <div className="ok-container py-20">Vacature niet gevonden.</div>;

  return (
    <section className="ok-section">
      <div className="ok-container">
        <Link to="/vacatures" className="inline-flex items-center gap-1.5 text-sm text-[#4a5d5e] hover:text-[#01696f]" data-testid="back-to-vacancies">
          <ArrowLeft size={14} /> Terug naar vacatures
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <span className="ok-chip">{v.expertise_category}</span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4 leading-[1.05]" data-testid="vacancy-title">{v.title}</h1>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#4a5d5e]">
              <span className="inline-flex items-center gap-1.5"><MapPin size={14} />{v.location} · {v.province}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={14} />{v.hours_per_week} uur/week</span>
              <span className="inline-flex items-center gap-1.5"><Briefcase size={14} />{v.duration_months} maanden</span>
              {v.start_date && <span className="inline-flex items-center gap-1.5"><Calendar size={14} />Start {v.start_date}</span>}
              {v.hourly_rate_max && <span className="inline-flex items-center gap-1.5"><Euro size={14} />Max € {v.hourly_rate_max}/u</span>}
            </div>

            <div className="mt-10 prose max-w-none">
              <h2 className="font-display text-2xl font-semibold mt-10">Opdracht</h2>
              <p className="text-[#0a191a] leading-relaxed mt-3 whitespace-pre-wrap">{v.description}</p>

              {v.knockout_criteria && (
                <>
                  <h2 className="font-display text-2xl font-semibold mt-10">Knock-out criteria</h2>
                  <div className="mt-4 border-l-4 border-[#01696f] bg-[#e6f0f1] p-5">
                    <p className="text-sm whitespace-pre-wrap">{v.knockout_criteria}</p>
                  </div>
                </>
              )}

              {v.preferred_criteria && (
                <>
                  <h2 className="font-display text-2xl font-semibold mt-10">Gunningscriteria</h2>
                  <div className="mt-4 bg-[#f7f6f2] p-5">
                    <p className="text-sm whitespace-pre-wrap">{v.preferred_criteria}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-[#e2e8f0] p-6 lg:sticky lg:top-24">
              <div className="ok-eyebrow mb-2">Reageren</div>
              <h3 className="font-display text-xl font-semibold">Snel handelen loont</h3>
              {v.deadline && (
                <p className="text-sm text-[#4a5d5e] mt-2">Deadline: <strong>{v.deadline}</strong></p>
              )}
              <div className="mt-6 space-y-3">
                {(!user || user === false || user.role === "candidate") && (
                  <button
                    onClick={apply}
                    disabled={busy}
                    data-testid="apply-btn"
                    className="w-full bg-[#01696f] text-white py-3 rounded-sm hover:bg-[#015257] disabled:opacity-60 font-medium"
                  >
                    {busy ? "Bezig…" : "Solliciteer"}
                  </button>
                )}
                {user && user.role === "candidate" && (
                  <button
                    onClick={save}
                    data-testid="save-btn"
                    className="w-full border border-[#0a191a] py-3 rounded-sm hover:bg-[#0a191a] hover:text-white font-medium"
                  >
                    Opslaan
                  </button>
                )}
                {user && user.role === "client" && (
                  <Link
                    to="/inlener-dashboard"
                    data-testid="propose-cta"
                    className="block w-full border border-[#0a191a] py-3 rounded-sm hover:bg-[#0a191a] hover:text-white font-medium text-center"
                  >
                    Kandidaat aanbieden
                  </Link>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-[#e2e8f0] text-xs text-[#4a5d5e] space-y-2">
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#01696f]" /> Direct contact met recruiter</div>
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#01696f]" /> AVG-veilig sollicitatieproces</div>
              </div>
            </div>

            {/* Themafoto per vakgebied */}
            <div className="mt-6 overflow-hidden border border-[#e2e8f0] aspect-[4/3] relative">
              <img
                src={imageForExpertise(v.expertise_category)}
                alt={`Themafoto ${v.expertise_category}`}
                className="w-full h-full object-cover"
                style={{ filter: "saturate(0.75) brightness(0.95)" }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                <div className="text-xs uppercase tracking-wider text-white/80 font-semibold">{v.expertise_category}</div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
